/**
 * One LIVE preload wave per connection at a time.
 *
 * `loadDatabasesFolder` lets an EXPLICIT refresh / Retry run while a preload is
 * in flight (the Retry tile must never be inert). That freedom has a cost the
 * guard used to pay for us: on a 30-database PostgreSQL server the first wave
 * is still fanning out at 2 databases in flight when the user hits Refresh, and
 * a second wave would double the per-database logins, flip `isPreloading` to
 * false while the other is still running, and write into tree nodes the first
 * wave is also rebuilding.
 *
 * So waves of the SAME generation queue instead of racing: the refresh's wave
 * starts when the one before it has settled. Serialising rather than skipping
 * is deliberate — the user asked for fresh data, and most of the second pass is
 * served from the query cache anyway.
 *
 * Serialisation stops at the generation boundary. A wave from a dead connect
 * (`connectionGenerations`) is neither waited on — the reconnect's work must
 * not queue behind a disconnected connection's leftovers — nor run at all if it
 * is still sitting in the queue when its generation ends.
 */

export interface ConnectionWaveQueue {
  /**
   * Runs `wave` after any wave of the SAME generation already queued for this
   * connection. Skipped entirely if `generation` is stale by the time its turn
   * comes. The returned promise settles when this call is done and never
   * rejects — a background preload failing must not become an unhandled
   * rejection at the call site.
   */
  enqueue: (connectionId: string, generation: number, wave: () => Promise<void>) => Promise<void>;
  /** Drops the bookkeeping for a connection that is gone. */
  forget: (connectionId: string) => void;
}

export interface ConnectionWaveQueueOptions {
  /** Generation check — a wave whose generation is stale is skipped. */
  isCurrent: (connectionId: string, generation: number) => boolean;
  onError?: (error: unknown) => void;
}

export function createConnectionWaveQueue({
  isCurrent,
  onError = (error) => console.error('[objectPreload] wave failed:', error),
}: ConnectionWaveQueueOptions): ConnectionWaveQueue {
  type Slot = { promise: Promise<void>; generation: number };
  const inFlight = new Map<string, Slot>();

  const enqueue = (
    connectionId: string,
    generation: number,
    wave: () => Promise<void>,
  ): Promise<void> => {
    const previous = inFlight.get(connectionId);
    // Only wait behind a wave of the same connect. An obsolete one is going to
    // go quiet on its own; the live work must not be held up by it.
    const waitFor = previous?.generation === generation ? previous.promise : undefined;

    const next = (async () => {
      // `waitFor` never rejects (this same wrapper produced it), so no catch.
      if (waitFor) await waitFor;
      // Re-checked HERE, not at enqueue time: the connection may have gone away
      // while this wave sat in the queue.
      if (!isCurrent(connectionId, generation)) return;
      try {
        await wave();
      } catch (error) {
        onError(error);
      }
    })();

    const slot: Slot = { promise: next, generation };
    inFlight.set(connectionId, slot);
    void next.then(() => {
      // Only the tail clears the slot: a wave queued behind this one has
      // already replaced the entry and is still pending.
      if (inFlight.get(connectionId) === slot) inFlight.delete(connectionId);
    });
    return next;
  };

  return {
    enqueue,
    forget: (connectionId) => {
      // Safe to drop even mid-wave: the generation bump that accompanies a
      // disconnect already silences whatever is still running.
      inFlight.delete(connectionId);
    },
  };
}
